import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronLeft, FlaskConical, Languages, Save } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { PERMISSIONS } from '@/lib/permissions'
import { useAuth } from '@/providers/AuthProvider'
import { useToast } from '@/providers/UiProviders'
import { EmptyState, PermissionDenied, Skeleton } from '@/components/States'
import {
  AUDIENCE_PRESETS,
  CAMP_STR as S,
  createBroadcast,
  fetchBroadcastsRaw,
  type BroadcastLite,
} from './campaignsApi'

const LANGS = [
  { key: 'uz', label: "O'zbekcha" },
  { key: 'ru', label: 'Русский' },
  { key: 'en', label: 'English' },
] as const
type LangKey = (typeof LANGS)[number]['key']
type LangDraft = Record<LangKey, { title: string; body: string }>

const emptyLangs = (): LangDraft => ({
  uz: { title: '', body: '' },
  ru: { title: '', body: '' },
  en: { title: '', body: '' },
})

const BackLink = () => (
  <Link
    to="/broadcasts"
    className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
  >
    <ChevronLeft size={13} />
    {S.back}
  </Link>
)

const AudienceSelect = ({ value, onChange }: { value: string; onChange: (v: string) => void }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    title={S.audience}
    className="rounded-md border border-border-default bg-surface-1 px-2 py-1.5 text-sm text-primary"
  >
    {AUDIENCE_PRESETS.map((a) => (
      <option key={a.key} value={a.key}>
        {a.label}
      </option>
    ))}
  </select>
)

/**
 * A15.05 — Ko'p tilli xabar muharriri. Til bo'yicha tab (uz/ru/en) + jonli
 * preview. Saqlash asosiy til matnini REAL draftga yozadi (POST /admin/broadcasts).
 */
export function MessageEditor() {
  const { can } = useAuth()
  const { toast } = useToast()
  const [langs, setLangs] = useState<LangDraft>(emptyLangs())
  const [tab, setTab] = useState<LangKey>('uz')
  const [primary, setPrimary] = useState<LangKey>('uz')
  const [audience, setAudience] = useState('all')
  const [busy, setBusy] = useState(false)

  if (!can(PERMISSIONS.USERS_VIEW)) return <PermissionDenied permission={PERMISSIONS.USERS_VIEW} />

  const cur = langs[tab]
  const setCur = (patch: Partial<{ title: string; body: string }>) =>
    setLangs((l) => ({ ...l, [tab]: { ...l[tab], ...patch } }))
  const filled = (k: LangKey) => langs[k].body.trim().length > 0

  const save = async () => {
    const p = langs[primary]
    if (!p.body.trim()) {
      toast(S.bodyRequired, { tone: 'error' })
      return
    }
    setBusy(true)
    try {
      await createBroadcast({
        ...(p.title.trim() ? { title: p.title.trim() } : {}),
        body: p.body.trim(),
        audience,
      })
      toast(S.draftSaved, { tone: 'success' })
      setLangs(emptyLangs())
    } catch (e) {
      toast(S.failed, { tone: 'error', ...(e instanceof Error && e.message ? { description: e.message } : {}) })
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <PageHeader
        title={S.editorTitle}
        screenId="A15.05"
        description={S.editorNote}
        actions={
          <div className="flex items-center gap-1.5">
            <Link
              to="/broadcasts/ab"
              className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
            >
              <FlaskConical size={13} />
              {S.abTitle}
            </Link>
            <BackLink />
          </div>
        }
      />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        <div className="mx-auto grid max-w-4xl gap-3 lg:grid-cols-2">
          <div className="space-y-2 rounded-lg border border-border-default bg-surface-2 p-3">
            <div className="flex items-center gap-1">
              {LANGS.map((l) => (
                <button
                  key={l.key}
                  onClick={() => setTab(l.key)}
                  className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-sm ${
                    tab === l.key ? 'bg-accent text-white' : 'text-secondary hover:bg-surface-3'
                  }`}
                >
                  <Languages size={12} />
                  {l.label}
                  {filled(l.key) && <span className={tab === l.key ? 'text-white' : 'text-success'}>●</span>}
                </button>
              ))}
            </div>
            <input
              value={cur.title}
              onChange={(e) => setCur({ title: e.target.value })}
              placeholder={S.titleField}
              className="w-full rounded-md border border-border-default bg-surface-1 px-3 py-2 text-sm text-primary outline-none focus:border-accent"
            />
            <textarea
              value={cur.body}
              onChange={(e) => setCur({ body: e.target.value })}
              placeholder={S.bodyField}
              rows={6}
              className="w-full rounded-md border border-border-default bg-surface-1 px-3 py-2 text-sm text-primary outline-none focus:border-accent"
            />
            <div className="flex flex-wrap items-center gap-2 border-t border-border-subtle pt-2">
              <label className="text-xs text-tertiary">{S.primaryLang}</label>
              <select
                value={primary}
                onChange={(e) => setPrimary(e.target.value as LangKey)}
                className="rounded-md border border-border-default bg-surface-1 px-2 py-1.5 text-sm text-primary"
              >
                {LANGS.map((l) => (
                  <option key={l.key} value={l.key}>
                    {l.label}
                  </option>
                ))}
              </select>
              <AudienceSelect value={audience} onChange={setAudience} />
              <button
                disabled={busy || !langs[primary].body.trim()}
                onClick={save}
                className="ml-auto inline-flex items-center gap-1.5 rounded-md border border-accent bg-accent-subtle px-3 py-1.5 text-sm text-accent hover:bg-accent hover:text-white disabled:opacity-50"
              >
                <Save size={13} />
                {S.saveDraft}
              </button>
            </div>
          </div>

          <div className="rounded-lg border border-border-default bg-surface-1 p-4">
            <div className="mb-2 text-xs font-medium uppercase tracking-wide text-tertiary">
              {S.preview} · {LANGS.find((l) => l.key === tab)?.label}
            </div>
            {cur.title.trim() && <div className="text-sm font-semibold text-primary">{cur.title}</div>}
            {cur.body.trim() ? (
              <div className="mt-1 whitespace-pre-wrap text-sm text-secondary">{cur.body}</div>
            ) : (
              <div className="mt-1 text-sm text-tertiary">—</div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

/**
 * A15.07 — A/B varianti. Ikki xabar (A/B) + taqsimot. Har biri alohida REAL
 * draft bo'lib saqlanadi (POST /admin/broadcasts, sarlavhaga «· A» / «· B»).
 * Pastda oldingi A/B juftliklari (fail-soft, mavjud broadcastlardan aniqlanadi).
 */
export function AbVariants() {
  const { can } = useAuth()
  const { toast } = useToast()
  const [a, setA] = useState({ title: '', body: '' })
  const [b, setB] = useState({ title: '', body: '' })
  const [audience, setAudience] = useState('all')
  const [split, setSplit] = useState(50)
  const [busy, setBusy] = useState(false)
  const [past, setPast] = useState<BroadcastLite[] | null>(null)
  const [tick, setTick] = useState(0)

  useEffect(() => {
    const ac = new AbortController()
    fetchBroadcastsRaw(ac.signal).then((rows) => setPast(rows.filter((r) => / · [AB]$/.test(r.title))))
    return () => ac.abort()
  }, [tick])

  if (!can(PERMISSIONS.USERS_VIEW)) return <PermissionDenied permission={PERMISSIONS.USERS_VIEW} />

  const create = async () => {
    if (!a.body.trim() || !b.body.trim()) {
      toast(S.bodyRequired, { tone: 'error' })
      return
    }
    setBusy(true)
    try {
      const base = (a.title.trim() || b.title.trim() || 'A/B').slice(0, 180)
      await createBroadcast({ title: `${base} · A`, body: a.body.trim(), audience })
      await createBroadcast({ title: `${base} · B`, body: b.body.trim(), audience })
      toast(S.abCreated, { tone: 'success' })
      setA({ title: '', body: '' })
      setB({ title: '', body: '' })
      setTick((t) => t + 1)
    } catch (e) {
      toast(S.failed, { tone: 'error', ...(e instanceof Error && e.message ? { description: e.message } : {}) })
    } finally {
      setBusy(false)
    }
  }

  const variantCard = (
    label: string,
    v: { title: string; body: string },
    set: (patch: Partial<{ title: string; body: string }>) => void,
  ) => (
    <div className="space-y-2 rounded-lg border border-border-default bg-surface-2 p-3">
      <div className="text-sm font-medium text-primary">{label}</div>
      <input
        value={v.title}
        onChange={(e) => set({ title: e.target.value })}
        placeholder={S.titleField}
        className="w-full rounded-md border border-border-default bg-surface-1 px-3 py-2 text-sm text-primary outline-none focus:border-accent"
      />
      <textarea
        value={v.body}
        onChange={(e) => set({ body: e.target.value })}
        placeholder={S.bodyField}
        rows={4}
        className="w-full rounded-md border border-border-default bg-surface-1 px-3 py-2 text-sm text-primary outline-none focus:border-accent"
      />
    </div>
  )

  return (
    <>
      <PageHeader
        title={S.abTitle}
        screenId="A15.07"
        description={S.abNote}
        actions={
          <div className="flex items-center gap-1.5">
            <Link
              to="/broadcasts/editor"
              className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
            >
              <Languages size={13} />
              {S.editorTitle}
            </Link>
            <BackLink />
          </div>
        }
      />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        <div className="mx-auto max-w-4xl space-y-3">
          <div className="grid gap-3 lg:grid-cols-2">
            {variantCard(S.variantA, a, (p) => setA((x) => ({ ...x, ...p })))}
            {variantCard(S.variantB, b, (p) => setB((x) => ({ ...x, ...p })))}
          </div>
          <div className="flex flex-wrap items-center gap-3 rounded-lg border border-border-default bg-surface-2 p-3">
            <AudienceSelect value={audience} onChange={setAudience} />
            <label className="flex items-center gap-2 text-sm text-secondary">
              {S.split}
              <input
                type="range"
                min={10}
                max={90}
                step={5}
                value={split}
                onChange={(e) => setSplit(Number(e.target.value))}
              />
              <span className="font-mono text-primary">
                {split}% / {100 - split}%
              </span>
            </label>
            <button
              disabled={busy || !a.body.trim() || !b.body.trim()}
              onClick={create}
              className="ml-auto inline-flex items-center gap-1.5 rounded-md border border-accent bg-accent-subtle px-3 py-1.5 text-sm text-accent hover:bg-accent hover:text-white disabled:opacity-50"
            >
              <FlaskConical size={13} />
              {S.createAb}
            </button>
          </div>

          <div>
            <div className="mb-2 text-xs font-medium uppercase tracking-wide text-tertiary">{S.pastAb}</div>
            {past === null ? (
              <Skeleton className="h-14 w-full" />
            ) : past.length === 0 ? (
              <EmptyState icon={<FlaskConical size={26} strokeWidth={1.5} />} title={S.empty} />
            ) : (
              <div className="space-y-1.5">
                {past.map((p) => (
                  <div
                    key={p.id}
                    className="flex items-center justify-between rounded-md border border-border-subtle bg-surface-2 px-3 py-2 text-sm"
                  >
                    <span className="text-primary">{p.title}</span>
                    <span className="text-xs text-tertiary">{new Date(p.created_at).toLocaleDateString()}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}
